import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Space,
  Popconfirm,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UnlockOutlined } from '@ant-design/icons';
import api from '../../utils/api';

interface User {
  id: number;
  email: string;
  role: 'admin' | 'viewer';
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
}

const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [pageSize, setPageSize] = useState(20);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error: any) {
      message.error('Failed to fetch users: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ role: 'viewer', is_active: true });
    setModalVisible(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    form.setFieldsValue({
      email: user.email,
      role: user.role,
      is_active: user.is_active,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/admin/users/${id}`);
      message.success('User deleted successfully');
      fetchUsers();
    } catch (error: any) {
      message.error('Failed to delete user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUnlockAccount = async (email: string) => {
    try {
      await api.post(`/auth/unlock/${encodeURIComponent(email)}`);
      message.success('Account unlocked');
      fetchUsers();
    } catch (error: any) {
      message.error('Failed to unlock account: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleResetPassword = async (id: number) => {
    Modal.confirm({
      title: 'Reset Password',
      content: (
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            try {
              await api.post(`/admin/users/${id}/reset-password`, {
                new_password: values.new_password,
              });
              message.success('Password reset successfully');
              Modal.destroyAll();
              form.resetFields();
            } catch (error: any) {
              message.error('Failed to reset password: ' + (error.response?.data?.detail || error.message));
            }
          }}
        >
          <Form.Item
            name="new_password"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
          >
            <Input.Password placeholder="Enter new password" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="Confirm Password"
            dependencies={['new_password']}
            rules={[
              { required: true, message: 'Please confirm new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Enter new password again" />
          </Form.Item>
        </Form>
      ),
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => form.submit(),
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingUser) {
        await api.patch(`/admin/users/${editingUser.id}`, values);
        message.success('User updated successfully');
      } else {
        // Create user requires password
        if (!values.password) {
          message.error('Password is required when creating a user');
          return;
        }
        await api.post('/admin/users', values);
        message.success('User created successfully');
      }
      setModalVisible(false);
      fetchUsers();
    } catch (error: any) {
      if (error.response?.status === 422) {
        const errors = error.response.data.detail;
        if (Array.isArray(errors)) {
          errors.forEach((err: any) => {
            form.setFields([
              {
                name: err.loc[err.loc.length - 1],
                errors: [err.msg],
              },
            ]);
          });
        }
      } else {
        message.error((editingUser ? 'Update' : 'Create') + ' failed: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (role === 'admin' ? 'Admin' : 'Viewer'),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (isActive ? 'Active' : 'Inactive'),
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      render: (date: string | null) => (date ? new Date(date).toLocaleString('en-US') : '-'),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('en-US'),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_: any, record: User) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleResetPassword(record.id)}
          >
            Reset Password
          </Button>
          <Popconfirm
            title="Are you sure you want to unlock this account?"
            onConfirm={() => handleUnlockAccount(record.email)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              size="small"
              icon={<UnlockOutlined />}
            >
              Unlock Account
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>User Management</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          New User
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (_page, size) => setPageSize(size),
              }}
      />

      <Modal
        title={editingUser ? 'Edit User' : 'New User'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Confirm"
        cancelText="Cancel"
        width={500}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email address' },
            ]}
          >
            <Input placeholder="Enter email" disabled={!!editingUser} />
          </Form.Item>
          {!editingUser && (
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please enter password' },
                { min: 6, message: 'Password must be at least 6 characters' },
              ]}
            >
              <Input.Password placeholder="Enter password" />
            </Form.Item>
          )}
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select placeholder="Select role">
              <Select.Option value="admin">Admin</Select.Option>
              <Select.Option value="viewer">Viewer</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUsersPage;
